import type {
  EnterpriseThemeContract,
  KindredConfig,
} from "@bestowinc/kindred-ds";
import { SHELL_CONFIG_ELEMENT_ID } from "@bestowinc/tinkertoy-core";
import { z } from "zod";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function plainObjectSchema<T extends object>() {
  return z.custom<T>((value): value is T => isPlainObject(value));
}

/**
 * Dev-only bundle overrides forwarded from the host's `ttv2-dev-override`
 * cookie. Each entry points the browser at a locally-served bundle so a host's
 * client renders the SAME local bundle its server already loads — without this,
 * the server honors the override but the browser still fetches the CDN.
 */
const bundleOverrideSchema = z.object({ bundleUrl: z.string() });
const devBundleOverridesSchema = z.object({
  views: z.record(bundleOverrideSchema),
  modules: z.record(bundleOverrideSchema),
});

const developerToolsCoreSchema = z.object({
  enabled: z.literal(true),
  shellVersion: z.string(),
  defaultShellVersion: z.string(),
  /** Absent and `null` both mean "no override active". */
  shellVersionOverride: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  availableShellVersions: z.array(z.string()),
  /**
   * Absent and `null` both mean "no override active". Scoped to a single
   * `branchId` — a session that crosses branches mid-flow must not carry the
   * override into a branch it was never meant to affect.
   */
  manifestVersionOverride: z
    .object({ branchId: z.string(), version: z.string() })
    .nullish()
    .transform((value) => value ?? null),
});

const developerToolsSchema = developerToolsCoreSchema.extend({
  devOverrides: devBundleOverridesSchema.optional(),
});

/** Detects host intent to enable dev tools before full config normalization. */
const developerToolsEnabledProbeSchema = z
  .object({
    developerTools: z.object({ enabled: z.literal(true) }).passthrough(),
  })
  .passthrough();

export type DevBundleOverrides = z.infer<typeof devBundleOverridesSchema>;
export type SerializedDeveloperToolsConfig = z.infer<typeof developerToolsSchema>;

function parseDeveloperToolsConfig(
  raw: unknown,
): SerializedDeveloperToolsConfig | undefined {
  const parsed = developerToolsSchema.safeParse(raw);
  if (parsed.success) {
    return parsed.data;
  }

  if (!isPlainObject(raw)) {
    return undefined;
  }

  const { devOverrides: nestedDevOverrides, ...coreFields } = raw;
  const parsedCore = developerToolsCoreSchema.safeParse(coreFields);
  if (!parsedCore.success) {
    console.warn(
      "[tinkertoy] Ignoring malformed developerTools config; the dev-tools pane will not mount.",
      parsedCore.error.flatten(),
    );
    return undefined;
  }

  const parsedNestedDevOverrides =
    devBundleOverridesSchema.safeParse(nestedDevOverrides);

  if (nestedDevOverrides !== undefined && !parsedNestedDevOverrides.success) {
    console.warn(
      "[tinkertoy] Ignoring malformed developerTools.devOverrides; the pane will still mount.",
      parsedNestedDevOverrides.error.flatten(),
    );
  }

  return {
    ...parsedCore.data,
    ...(parsedNestedDevOverrides.success
      ? { devOverrides: parsedNestedDevOverrides.data }
      : {}),
  };
}

const serializedProductAnalyticsConfigSchema = z.object({
  enabled: z.boolean(),
  metadata: z.record(z.unknown()),
});

export type SerializedProductAnalyticsConfig = z.infer<
  typeof serializedProductAnalyticsConfigSchema
>;

const enterpriseThemeSchema = plainObjectSchema<EnterpriseThemeContract>();
const kindredConfigSchema = plainObjectSchema<KindredConfig>();

const environmentSchema = z.record(z.string().optional());

const wireSerializedShellConfigSchema = z.object({
  apiBaseUrl: z.string(),
  cdnBaseUrl: z.string(),
  theme: enterpriseThemeSchema,
  analytics: z.unknown().optional(),
  env: z.unknown().optional(),
  devOverrides: z.unknown().optional(),
  kindredConfig: z.unknown().optional(),
  developerTools: z.unknown().optional(),
  defaultDocumentTitle: z.string().nullish(),
});

/**
 * Per-host runtime config a host app serializes into the shell HTML for the
 * browser entry to read on boot.
 *
 * The shell bundle is built once and served verbatim by every host
 * (enrollment-composer, verify, the microsites, ...), so the values that differ
 * per host travel here rather than being baked into the bundle:
 *
 * - `apiBaseUrl` — the host's same-origin Tinkertoy API mount. The shell fetches
 *   `${apiBaseUrl}/load` and posts `${apiBaseUrl}/action` against it. EC mounts
 *   this at `/composer/tinkertoy`; the standalone demo uses `/api/tinkertoy`.
 * - `cdnBaseUrl` — the CDN the module loader resolves view/module bundles from.
 * - `theme` — the resolved carrier theme handed to the shell's `ThemeProvider`.
 *   Resolved per request per carrier by the host, mirroring how EC, verify, and
 *   the microsites each resolve their own theme and hand it in (see
 *   {@link ShellConfig}).
 *
 * @see SHELL_CONFIG_ELEMENT_ID for the injection contract.
 */
export interface SerializedShellConfig {
  apiBaseUrl: string;
  cdnBaseUrl: string;
  theme: EnterpriseThemeContract;
  analytics?: SerializedProductAnalyticsConfig;
  /**
   * Optional shared runtime env vars the host exposes as the browser global
   * `ENV` (`MODE`/`ORG_CODE`/`PARTNER_ID`, …). The shell entry assigns this to
   * `globalThis.ENV` so bundled code that reads a Remix-style `window.ENV`
   * works. Absent for the standalone demo, whose entry then leaves `ENV` unset.
   */
  env?: Record<string, string | undefined>;
  /**
   * Optional dev-only bundle overrides (from the host's `ttv2-dev-override`
   * cookie). The shell entry threads these into the module/view loaders so the
   * browser loads locally-served bundles for overridden ids. Absent in normal
   * operation and for the standalone demo.
   */
  devOverrides?: DevBundleOverrides;
  /**
   * Carrier Kindred config for {@link KindredHead} and
   * {@link KindredConfigProvider}. Omitted when the host cannot resolve
   * platform-config tokens, matching Remix apps that skip `<KindredHead>`
   * in that case.
   */
  kindredConfig?: KindredConfig;
  /**
   * Non-production developer tools metadata. When `enabled` is false or the
   * field is absent, the in-shell developer tools pane is not mounted.
   */
  developerTools?: SerializedDeveloperToolsConfig;
  /**
   * Carrier-appropriate base document title for pre-flow errors and the initial
   * `/load` in-flight window. Injected by the host alongside the serialized
   * config so {@link ShellErrorBoundary} can fall back when `flowMeta` is absent.
   */
  defaultDocumentTitle?: string;
}

function normalizeSerializedShellConfig(
  config: z.infer<typeof wireSerializedShellConfigSchema>,
): SerializedShellConfig {
  const {
    analytics,
    defaultDocumentTitle,
    devOverrides,
    developerTools,
    env,
    kindredConfig,
    ...requiredConfig
  } = config;
  const parsedAnalytics =
    serializedProductAnalyticsConfigSchema.safeParse(analytics);
  const parsedDevOverrides = devBundleOverridesSchema.safeParse(devOverrides);
  const parsedKindredConfig = kindredConfigSchema.safeParse(kindredConfig);
  const parsedDeveloperTools =
    developerTools === undefined
      ? undefined
      : parseDeveloperToolsConfig(developerTools);
  const parsedEnvironment = environmentSchema.safeParse(env);

  return {
    ...requiredConfig,
    ...(defaultDocumentTitle != null ? { defaultDocumentTitle } : {}),
    ...(parsedAnalytics.success ? { analytics: parsedAnalytics.data } : {}),
    ...(parsedEnvironment.success ? { env: parsedEnvironment.data } : {}),
    ...(parsedDevOverrides.success
      ? { devOverrides: parsedDevOverrides.data }
      : {}),
    ...(parsedKindredConfig.success
      ? { kindredConfig: parsedKindredConfig.data }
      : {}),
    ...(parsedDeveloperTools !== undefined
      ? { developerTools: parsedDeveloperTools }
      : {}),
  };
}

/**
 * Read the host-injected {@link SerializedShellConfig} from the DOM.
 *
 * Returns `null` when the config element is absent or malformed — which is the
 * expected case for the standalone demo shell, whose entry then falls back to
 * its own demo defaults. A present-but-malformed blob also yields `null` rather
 * than throwing, so a serialization bug degrades to the fallback instead of a
 * white screen with no shell mounted at all.
 *
 * Browser telemetry settings in the same JSON blob are ignored; the shell entry
 * parses them separately.
 */
export function readSerializedShellConfig(): SerializedShellConfig | null {
  const element = document.getElementById(SHELL_CONFIG_ELEMENT_ID);
  if (!element?.textContent) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(element.textContent);
    const config = wireSerializedShellConfigSchema.safeParse(parsed);
    if (!config.success) {
      return null;
    }

    return normalizeSerializedShellConfig(config.data);
  } catch {
    return null;
  }
}

/**
 * Warn when the host serialized `developerTools.enabled: true` but config
 * normalization dropped it — usually a schema mismatch in nested fields.
 */
export function warnIfDeveloperToolsDroppedDuringParse(
  rawConfigText: string | null | undefined,
  parsed: SerializedShellConfig | null,
): void {
  if (rawConfigText == null || parsed?.developerTools?.enabled === true) {
    return;
  }

  try {
    const raw: unknown = JSON.parse(rawConfigText);
    if (developerToolsEnabledProbeSchema.safeParse(raw).success) {
      console.warn(
        "[tinkertoy] Host injected developerTools.enabled=true but the shell dropped it during config parse. The dev-tools pane will not mount.",
      );
    }
  } catch {
    // Ignore malformed host config when emitting the diagnostic.
  }
}
